import Hero from "../components/Hero";
import hotelImg from "../assests/images/hotel-bg.jpg";

import {
  FaHotel,
  FaPlane,
  FaCar,
  FaMapMarkedAlt,
  FaHeadset,
  FaShieldAlt,
  FaUtensils,
  FaPassport,
  FaArrowRight,
  FaCheck
} from "react-icons/fa";

import CTASection from "../components/CTASection";
import { Helmet } from "react-helmet-async";

export default function Services() {
  const services = [
    {
      icon: <FaHotel />,
      number: "01",
      title: "Hotel Booking",
      desc: "Book carefully selected hotels at competitive prices with a simple and reliable booking experience.",
      link: "https://jumeiradiscounthotels.com/",
    },
    {
      icon: <FaPlane />,
      number: "02",
      title: "Flight Booking",
      desc: "Find convenient and affordable flight options for your next journey with flexible travel choices.",
      link: "https://southtravels.com/flights",
      popular: true,
    },
    {
      icon: <FaPassport />,
      number: "03",
      title: "Visa Services",
      desc: "Get professional visa assistance, document guidance and support throughout your application process.",
      link: "https://southtravels.com/",
      popular: true,
    },
    {
      icon: <FaMapMarkedAlt />,
      number: "04",
      title: "Tour Packages",
      desc: "Discover curated tours and travel experiences designed around popular destinations and attractions.",
      link: "https://tourismindubai.com/",
    },
    {
      icon: <FaUtensils />,
      number: "05",
      title: "Food & Dining",
      desc: "Explore memorable dining experiences and discover restaurants for every taste and occasion.",
      link: "https://dubaicitytour.com/",
    },
    {
      icon: <FaCar />,
      number: "06",
      title: "Car Rentals",
      desc: "Choose from convenient car rental options to make getting around Dubai comfortable and easy.",
      link: "https://southtravels.com/car-rental-from-dubai",
    },
  ];

  const features = [
    {
      icon: <FaShieldAlt />,
      title: "Secure & Reliable",
      desc: "Your booking experience is designed with security and reliability in mind.",
    },
    {
      icon: <FaHeadset />,
      title: "24/7 Assistance",
      desc: "Our support team is available to help whenever you need assistance.",
    },
    {
      icon: <FaHotel />,
      title: "Trusted Hotels",
      desc: "Discover carefully selected hotels in popular destinations and locations.",
    },
    {
      icon: <FaMapMarkedAlt />,
      title: "Travel Made Easy",
      desc: "Bring hotels, travel services and experiences together in one place.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>
          Travel & Hotel Services | Jumeira Discount Hotels
        </title>

        <meta
          name="description"
          content="Explore affordable hotel booking, budget travel planning, holiday packages, visa assistance, car rentals and travel services with Jumeira Discount Hotels."
        />

        <link
          rel="canonical"
          href="https://jumeiradiscounthotels.com/services"
        />

        <meta
          property="og:title"
          content="Travel & Hotel Services | Jumeira Discount Hotels"
        />

        <meta
          property="og:description"
          content="Explore hotel bookings, flights, visa services, tours, dining and car rentals with Jumeira Discount Hotels."
        />

        <meta
          property="og:url"
          content="https://jumeiradiscounthotels.com/services"
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Travel & Hotel Services | Jumeira Discount Hotels"
        />

        <meta
          name="twitter:description"
          content="Explore hotel bookings, flights, visa services, tours, dining and car rentals with Jumeira Discount Hotels."
        />

        <meta
          name="robots"
          content="index, follow"
        />
      </Helmet>

      <div className="w-full bg-slate-50">

        {/* HERO */}
        <Hero
          variant="inner"
          title="Our Services"
          subtitle="Everything you need for a smoother, smarter and more enjoyable journey."
          backgroundImage={hotelImg}
        />

        {/* INTRO */}
        <section className="py-16 sm:py-20 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            <div className="max-w-3xl mx-auto text-center">

              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E8F0F3] text-[#174B5F] text-xs sm:text-sm font-bold uppercase tracking-wider">
                <span className="w-2 h-2 rounded-full bg-[#D4A83F]"></span>
                Everything In One Place
              </span>

              <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
                Travel Services Designed
                <span className="block text-[#174B5F]">
                  Around You
                </span>
              </h2>

              <p className="mt-5 text-slate-500 text-base sm:text-lg leading-7 max-w-2xl mx-auto">
                From finding the right hotel to arranging flights,
                visas, tours and transportation, our services help
                make your travel planning simple and stress-free.
              </p>

            </div>

          </div>
        </section>


        {/* SERVICES */}
        <section className="pb-20 sm:pb-24 lg:pb-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Section heading */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-10">

              <div>
                <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#D4A83F]">
                  What We Offer
                </p>

                <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-slate-900">
                  Services For Every Journey
                </h2>
              </div>

              <p className="max-w-md text-sm sm:text-base text-slate-500 leading-6">
                Explore our range of travel services and choose
                exactly what you need for your next trip.
              </p>

            </div>


            {/* Service cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">

              {services.map((service, index) => (
                <a
                  href={service.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={index}
                  className="
                                        group
                                        relative
                                        bg-white
                                        rounded-[26px]
                                        border border-slate-200
                                        p-6 sm:p-7
                                        overflow-hidden
                                        shadow-[0_8px_30px_rgba(15,23,42,0.05)]
                                        hover:shadow-[0_20px_50px_rgba(15,23,42,0.12)]
                                        hover:-translate-y-2
                                        transition-all
                                        duration-500
                                    "
                >

                  {/* Background decoration */}
                  <div className="
                                        absolute
                                        -right-16
                                        -top-16
                                        w-40
                                        h-40
                                        rounded-full
                                        bg-[#E8F0F3]
                                        group-hover:bg-[#D4A83F]/10
                                        transition-colors
                                        duration-500
                                    "></div>


                  {/* Popular */}
                  {service.popular && (
                    <div className="
                                            absolute
                                            top-5
                                            right-5
                                            px-3
                                            py-1
                                            rounded-full
                                            bg-[#FFF5D9]
                                            text-[#9A751B]
                                            text-[10px]
                                            font-bold
                                            uppercase
                                            tracking-wider
                                        ">
                      Popular
                    </div>
                  )}


                  {/* Number */}
                  <div className="
                                        absolute
                                        top-6
                                        right-7
                                        text-5xl
                                        font-black
                                        text-slate-100
                                        group-hover:text-slate-200
                                        transition
                                    ">
                    {service.number}
                  </div>


                  {/* Icon */}
                  <div className="
                                        relative
                                        w-16
                                        h-16
                                        rounded-2xl
                                        bg-[#E8F0F3]
                                        text-[#174B5F]
                                        flex
                                        items-center
                                        justify-center
                                        text-2xl
                                        group-hover:bg-gradient-to-br
                                        group-hover:from-[#002B45]
                                        group-hover:via-[#174B5F]
                                        group-hover:to-[#D4A83F]
                                        group-hover:text-white
                                        group-hover:rotate-3
                                        transition-all
                                        duration-500
                                    ">
                    {service.icon}
                  </div>


                  {/* Content */}
                  <div className="relative mt-6">

                    <h3 className="
                                            text-xl
                                            font-bold
                                            text-slate-900
                                            group-hover:text-[#174B5F]
                                            transition
                                        ">
                      {service.title}
                    </h3>

                    <p className="
                                            mt-3
                                            text-sm
                                            text-slate-500
                                            leading-6
                                            min-h-[72px]
                                        ">
                      {service.desc}
                    </p>


                    {/* Link */}
                    <div className="
                                            mt-6
                                            flex
                                            items-center
                                            gap-2
                                            text-sm
                                            font-bold
                                            text-[#174B5F]
                                        ">
                      Explore Service

                      <span className="
                                                w-8
                                                h-8
                                                rounded-full
                                                bg-slate-100
                                                flex
                                                items-center
                                                justify-center
                                                group-hover:bg-[#174B5F]
                                                group-hover:text-white
                                                group-hover:translate-x-1
                                                transition-all
                                            ">
                        <FaArrowRight className="text-xs" />
                      </span>
                    </div>

                  </div>

                </a>
              ))}

            </div>

          </div>
        </section>


        {/* WHY CHOOSE US */}
        <section className="relative overflow-hidden bg-[#071E2C] py-20 sm:py-24 lg:py-28">

          {/* Decorative circles */}
          <div className="
                        absolute
                        -top-32
                        -right-32
                        w-80
                        h-80
                        rounded-full
                        bg-[#174B5F]/40
                        blur-3xl
                    "></div>

          <div className="
                        absolute
                        -bottom-32
                        -left-32
                        w-80
                        h-80
                        rounded-full
                        bg-[#D4A83F]/10
                        blur-3xl
                    "></div>


          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Heading */}
            <div className="max-w-2xl mb-12">

              <span className="
                                inline-flex
                                items-center
                                gap-2
                                px-4
                                py-2
                                rounded-full
                                bg-white/10
                                border
                                border-white/10
                                text-[#D4A83F]
                                text-xs
                                font-bold
                                uppercase
                                tracking-wider
                            ">
                Why Travel With Us
              </span>

              <h2 className="
                                mt-5
                                text-3xl
                                sm:text-4xl
                                lg:text-5xl
                                font-bold
                                !text-white
                            ">
                More Than Just
                <span className="text-[#D4A83F]">
                  {" "}Travel Services
                </span>
              </h2>

              <p className="
                                mt-4
                                text-white/60
                                leading-7
                                max-w-xl
                            ">
                We focus on making every part of your travel
                experience easier, more comfortable and more reliable.
              </p>

            </div>


            {/* Feature cards */}
            <div className="
                            grid
                            grid-cols-1
                            sm:grid-cols-2
                            lg:grid-cols-4
                            gap-4
                            lg:gap-5
                        ">

              {features.map((item, index) => (
                <div
                  key={index}
                  className="
                                        group
                                        relative
                                        bg-white/[0.06]
                                        border
                                        border-white/10
                                        rounded-[24px]
                                        p-6
                                        hover:bg-white/[0.10]
                                        hover:-translate-y-2
                                        transition-all
                                        duration-500
                                    "
                >

                  <div className="
                                        w-12
                                        h-12
                                        rounded-xl
                                        bg-[#D4A83F]/10
                                        text-[#D4A83F]
                                        flex
                                        items-center
                                        justify-center
                                        text-xl
                                        group-hover:bg-[#D4A83F]
                                        group-hover:text-[#071E2C]
                                        transition-all
                                    ">
                    {item.icon}
                  </div>

                  <h3 className="
                                        mt-6
                                        text-lg
                                        font-bold
                                        text-white
                                    ">
                    {item.title}
                  </h3>

                  <p className="
                                        mt-3
                                        text-sm
                                        text-white/55
                                        leading-6
                                    ">
                    {item.desc}
                  </p>

                  <div className="
                                        mt-5
                                        flex
                                        items-center
                                        gap-2
                                        text-xs
                                        font-semibold
                                        text-white/40
                                    ">
                    <FaCheck className="text-[#D4A83F]" />
                    Travel with confidence
                  </div>

                </div>
              ))}

            </div>

          </div>
        </section>


        {/* CTA */}
        <CTASection />

      </div>
    </>
  );
}